<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'primevue/button';
import { reportError } from './reportError';

const failure = ref<Error | null>(null);
const slotKey = ref(0);

const route = useRoute();
watch(() => route.fullPath, () => { failure.value = null; });

onErrorCaptured((err, _instance, info) => {
  const error = err instanceof Error ? err : new Error(String(err));
  console.error('[ErrorBoundary]', error);
  failure.value = error;
  reportError(error, info);
  return false;
});

function retry(): void {
  failure.value = null;
  slotKey.value += 1;
}
</script>

<template>
  <div
    v-if="failure"
    class="error-boundary"
    role="alert"
    data-testid="error-boundary"
  >
    <h1 class="error-boundary__title">
      Something went wrong
    </h1>
    <p class="error-boundary__body">
      We hit an unexpected error. Try again, and if it keeps happening, refresh the page.
    </p>
    <Button
      label="Try again"
      severity="secondary"
      data-testid="error-boundary__retry"
      @click="retry"
    />
  </div>
  <slot v-else :key="slotKey" />
</template>

<style scoped>
.error-boundary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--app-space-16);
  padding: var(--app-space-32);
  text-align: center;
  flex: 1;
  background: var(--app-background-primary);
  color: var(--app-text-primary);
}
.error-boundary__title {
  margin: 0;
  font-size: var(--app-font-size-h2);
  line-height: var(--app-line-height-h2);
  font-weight: var(--app-font-weight-semibold);
}
.error-boundary__body {
  margin: 0;
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  color: var(--app-text-muted);
  max-width: 36ch;
}
</style>
